import Script from "next/script";
import { useCallback, useEffect, useMemo } from "react";

const BASE_URL = process.env.NEXT_PUBLIC_APP_CONTACT_US_DOMAIN;
const TOKEN = process.env.NEXT_PUBLIC_APP_CONTACT_US_WEBSITE_TOKEN;
const SDK_URL = `${BASE_URL}/packs/js/sdk.js`;

type ChatwootLoaderProps = {
	locale: string;
	settings?: Record<string, any>;
	autoOpen?: boolean;
	onReady?: () => void;
	onError?: (source: "event" | "script" | "init", error?: unknown) => void;
};

const normalizeLocale = (locale: string) => {
	const normalized = (locale || "en").toLowerCase();
	switch (normalized) {
		case "zh-hans":
			return "zh_CN";
		case "zh-hant":
			return "zh_TW";
		default:
			return locale;
	}
};

export default function ChatwootLoader({ locale, settings, autoOpen = false, onReady, onError }: ChatwootLoaderProps) {
	const mergedSettings = useMemo(
		() => ({
			hideMessageBubble: true,
			useBrowserLanguage: false,
			locale: normalizeLocale(locale),
			...settings,
		}),
		[locale, settings],
	);

	const handleReadyEvent = useCallback(() => {
		if (autoOpen && typeof window !== "undefined" && window.$chatwoot) {
			window.$chatwoot.toggle("open");
		}
		onReady?.();
	}, [autoOpen, onReady]);

	const handleError = useCallback(
		(source: "event" | "script" | "init", error?: unknown) => {
			onError?.(source, error);
		},
		[onError],
	);

	const handleEventError = useCallback(
		(event: Event) => {
			handleError("event", event);
		},
		[handleError],
	);

	const runSDK = useCallback(() => {
		if (typeof window === "undefined") return;

		window.chatwootSettings = mergedSettings;
		window.chatwootSDK?.run({
			websiteToken: TOKEN,
			baseUrl: BASE_URL,
		});

		window.addEventListener("chatwoot:ready", handleReadyEvent);
		window.addEventListener("chatwoot:error", handleEventError);
	}, [handleEventError, handleReadyEvent, mergedSettings]);

	useEffect(() => {
		return () => {
			if (typeof window === "undefined") return;
			window.removeEventListener("chatwoot:ready", handleReadyEvent);
			window.removeEventListener("chatwoot:error", handleEventError);
		};
	}, [handleEventError, handleReadyEvent]);

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (window.chatwootSettings) {
			window.chatwootSettings.locale = mergedSettings.locale;
		}
	}, [mergedSettings.locale]);

	return (
		<Script
			src={SDK_URL}
			defer
			async
			onLoad={() => {
				try {
					runSDK();
				} catch (error) {
					handleError("init", error);
				}
			}}
			onError={(error) => {
				handleError("script", error);
			}}
		/>
	);
}
